import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactFormRefs
 * @property {HTMLElement} success - Success message shown once the enquiry is filed
 */

/**
 * Sends the contact form to the Intercom ticket endpoint instead of Shopify.
 * If the endpoint is missing or fails, the form submits to Shopify as normal
 * so the enquiry is never lost.
 *
 * @extends {Component<ContactFormRefs>}
 */
class ContactFormComponent extends Component {
  requiredRefs = ['success'];

  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector('form');
    if (!form) return;
    // Shopify's storefront captcha script re-submits every /contact form to Shopify,
    // even after preventDefault, which sent each enquiry twice. Opt out only while
    // this script handles the submit, so the plain form keeps Shopify's protection.
    if (this.dataset.endpoint) form.dataset.nocaptcha = 'true';
    form.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.querySelector('form')?.removeEventListener('submit', this.#handleSubmit);
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const endpoint = this.dataset.endpoint;
    const form = event.target;
    if (!endpoint || !(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    const data = new FormData(form);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.get('contact[name]'),
          email: data.get('contact[email]'),
          message: data.get('contact[body]'),
          website: data.get('website'),
          turnstile: data.get('cf-turnstile-response'),
        }),
      });
      if (!response.ok) throw new Error(`Contact endpoint responded ${response.status}`);

      form.hidden = true;
      this.refs.success.hidden = false;
      this.refs.success.focus();
    } catch (error) {
      console.error(error);
      // Fall back to Shopify's own contact form handling.
      form.submit();
    }
  };
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
